import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { WaitlistEntry } from "@/lib/waitlist";

interface Props {
  onToast: (type: "success" | "error", message: string) => void;
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportWaitlistCsv(rows: WaitlistEntry[]): void {
  const headers = ["nome", "email", "whatsapp", "tipo", "status", "data"];
  const lines = [headers.join(",")];
  rows.forEach((e) => {
    const cells = [
      e.name || "",
      e.email || "",
      e.whatsapp || "",
      e.member_id || e.source === "membro" ? "Membro" : "Visitante",
      e.status === "convidada" ? "Convidada" : "Aguardando",
      e.created_at ? new Date(e.created_at).toLocaleDateString("pt-BR") : "",
    ].map((v) => csvEscape(v));
    lines.push(cells.join(","));
  });
  const csv = "\uFEFF" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `lista-de-espera-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function WaitlistManager({ onToast }: Props) {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "membro" | "visitante">("all");

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("event_waitlist")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setEntries(data as WaitlistEntry[]);
    }
    setLoading(false);
  }

  async function markInvited(id: number) {
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("event_waitlist")
      .update({ status: "convidada", notified_at: now })
      .eq("id", id);

    if (!error) {
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: "convidada", notified_at: now } : e)),
      );
      onToast("success", "Marcada como convidada!");
    } else {
      onToast("error", "Falha ao marcar como convidada.");
    }
  }

  // apaga de vez (cadastro repetido ou de teste): pede confirmação porque não tem como desfazer
  async function removeEntry(entry: WaitlistEntry) {
    if (!window.confirm(`Apagar ${entry.name} da lista "Quero ser avisada"? Isso não pode ser desfeito.`)) return;
    const { error } = await supabase.from("event_waitlist").delete().eq("id", entry.id);

    if (!error) {
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      onToast("success", `${entry.name} foi apagada da lista.`);
    } else {
      onToast("error", "Falha ao apagar da lista.");
    }
  }

  const normalizedSearch = normalize(searchTerm.trim());
  const filtered = entries.filter((e) => {
    const isMember = !!e.member_id || e.source === "membro";
    if (typeFilter === "membro" && !isMember) return false;
    if (typeFilter === "visitante" && isMember) return false;
    if (!normalizedSearch) return true;
    const haystack = normalize([e.name, e.email, e.whatsapp || ""].filter(Boolean).join(" "));
    return haystack.includes(normalizedSearch);
  });

  const invitedCount = entries.filter((e) => e.status === "convidada").length;
  const waitingCount = entries.length - invitedCount;
  const memberCount = entries.filter((e) => !!e.member_id || e.source === "membro").length;
  const visitorCount = entries.length - memberCount;

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 mb-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <i className="ri-notification-3-line text-primary" />
        </div>
        <div>
          <h4 className="font-display text-base text-brown">Quero ser avisada</h4>
          <p className="font-body text-xs text-brown/60 mt-0.5">
            Contatos que pediram para ser avisadas do próximo encontro.
          </p>
        </div>
      </div>

      {/* Busca, contador e exportação */}
      <div className="flex flex-col lg:flex-row gap-3 md:gap-4 mb-5">
        <div className="flex-1 relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-brown/40" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome, e-mail ou WhatsApp..."
            className="w-full pl-11 pr-10 py-3 rounded-xl border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-brown/10 transition-colors cursor-pointer"
              title="Limpar busca"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>

        <button
          onClick={() => exportWaitlistCsv(filtered)}
          disabled={filtered.length === 0}
          className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          title="Exportar a lista em CSV"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-download-line" />
          </div>
          <span>Exportar CSV</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="font-body text-sm text-brown/60 whitespace-nowrap">Filtrar:</span>
        <button
          onClick={() => setTypeFilter("all")}
          className={`px-3 py-1.5 rounded-full font-body text-sm border transition-colors whitespace-nowrap cursor-pointer ${
            typeFilter === "all"
              ? "bg-primary/15 text-primary border-primary/30 font-semibold"
              : "bg-white border-nude text-brown/60 hover:bg-beige"
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => setTypeFilter("membro")}
          className={`px-3 py-1.5 rounded-full font-body text-sm border transition-colors whitespace-nowrap cursor-pointer ${
            typeFilter === "membro"
              ? "bg-primary/15 text-primary border-primary/30 font-semibold"
              : "bg-white border-nude text-brown/60 hover:bg-beige"
          }`}
        >
          Membros ({memberCount})
        </button>
        <button
          onClick={() => setTypeFilter("visitante")}
          className={`px-3 py-1.5 rounded-full font-body text-sm border transition-colors whitespace-nowrap cursor-pointer ${
            typeFilter === "visitante"
              ? "bg-brown/15 text-brown border-brown/30 font-semibold"
              : "bg-white border-nude text-brown/60 hover:bg-beige"
          }`}
        >
          Visitantes ({visitorCount})
        </button>
      </div>

      <p className="font-body text-sm text-brown/60 mb-5">
        <strong className="text-primary">{entries.length}</strong> contato(s) no total ·{" "}
        <strong className="text-primary">{memberCount}</strong> membro(s) ·{" "}
        <strong className="text-brown">{visitorCount}</strong> visitante(s) ·{" "}
        <strong className="text-amber-600">{waitingCount}</strong> aguardando ·{" "}
        <strong className="text-green-600">{invitedCount}</strong> convidada(s)
      </p>

      {loading ? (
        <div className="flex items-center gap-3 py-6">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="font-body text-sm text-brown/60">Carregando...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10">
          <i className="ri-inbox-line text-3xl text-brown/20 mb-3" />
          <p className="font-body text-sm text-brown/60">
            {entries.length === 0
              ? "Nenhum contato na lista de espera ainda."
              : "Nenhum contato encontrado para esta busca."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const isMember = !!e.member_id || e.source === "membro";
            const isInvited = e.status === "convidada";

            return (
              <div
                key={e.id}
                className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center rounded-xl border border-nude/70 p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h5 className="font-body text-sm font-semibold text-brown">{e.name}</h5>
                    <span
                      className={`px-2 py-0.5 rounded-full font-body text-[11px] uppercase tracking-wide ${
                        isMember ? "bg-primary/10 text-primary" : "bg-brown/10 text-brown/60"
                      }`}
                    >
                      {isMember ? "Membro" : "Visitante"}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full font-body text-[11px] uppercase tracking-wide ${
                        isInvited ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {isInvited ? "Convidada" : "Aguardando"}
                    </span>
                  </div>
                  <p className="font-body text-sm text-brown/70 mt-1 break-all">{e.email}</p>
                  {e.whatsapp && (
                    <p className="font-body text-xs text-brown/50 mt-0.5">
                      <i className="ri-whatsapp-line mr-1" />
                      {e.country_code ? `${e.country_code} ` : ""}{e.whatsapp}
                    </p>
                  )}
                  <p className="font-body text-xs text-brown/40 mt-1">
                    {new Date(e.created_at).toLocaleString("pt-BR")}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 shrink-0">
                  {!isInvited && (
                    <button
                      onClick={() => markInvited(e.id)}
                      className="px-4 py-2 rounded-lg bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-check-line mr-1" /> Marcar como convidada
                    </button>
                  )}
                  <button
                    onClick={() => removeEntry(e)}
                    className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-700 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1" /> Apagar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}